import React, { useEffect, useState } from "react";
import getConfig from "next/config";
import styled from "styled-components";
import axios from "axios";
import { isMobile } from "react-device-detect";
import { Form, Row, Col, OverlayTrigger, Tooltip } from "react-bootstrap";

import { FORM_FIELD_KEYS } from "../../../constants/forms";
import { getFieldProps, getDayOptions } from "../../../utils/form-fields";
import { getFileExtension, getFileExtensionsString, checkIfFileAllowed } from "../../../utils/file";

import TextInput from "../../controls/TextInput";
import Upload from "../../controls/Upload";
import Select from "../../controls/Select";
import StyledButton from "../../styled/StyledButton";

const { publicRuntimeConfig } = getConfig();

const getYearOptions = () => {
  const startYear = new Date().getFullYear() - 100;

  return Array.from(new Array(101), (_, i) => {
    return { value: startYear + i, title: `${startYear + i}` };
  }).reverse();
};

const StyledBOIUpgradeForm = styled(Form)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .content-body {
    display: flex;
    flex-direction: column;
    align-items: center;

    flex-grow: 1;
    width: 100%;

    .content-block {
      display: flex;
      flex-direction: column;
      align-items: center;

      width: 100%;

      padding-top: 14px;
      padding-bottom: 14px;
      padding-left: 14px;
      padding-right: 14px;

      font-family: "MerriweatherLight";
      font-size: 16px;
      line-height: 26px;
      color: #1b3858;

      &:nth-child(1) {
        padding-top: 0;
        font-size: 22px;
        line-height: 32px;

        > span {
          margin-bottom: 20px;
        }
      }

      &.divider {
        border-bottom: 1px solid #e7e7e7;
      }

      > span {
        display: inline-block;
        text-align: center;
      }

      ul {
        margin-top: 10px;

        li {
          margin-bottom: 16px;
          font-size: 16px;
        }

        li::marker {
          content: "»   ";
          font-size: 20px;
        }

        li:last-child {
          margin-bottom: 0;
        }
      }

      button {
        width: 100%;
        height: 52px;
      }
    }
  }

  .divider {
    height: 16px;
    width: 100%;
  }

  @media ${({ theme }) => theme.devices.tablet} {
    .content-body {
      width: 100%;
      padding-bottom: 0;

      .content-block {
        padding-top: 12px;
        padding-bottom: 12px;

        font-size: 16px;
        line-height: 26px;

        > span {
          padding-left: 0;
          padding-right: 0;
        }

        &:nth-child(1) {
          font-size: 20px;
          line-height: 30px;
        }

        ul {
          li {
            font-size: 16px;
          }
        }
      }
    }
  }
`;

const StyledRow = styled(Row)`
  width: 100%;
`;

const StyledImg = styled.img`
  margin-bottom: 4px;
  cursor: pointer;
`;

function BOIUpgradeForm({ values = {}, onChange, onCancel, onConfirm, onSubmit }) {
  const [allowedExtensionsString, setAllowedExtensionsString] = useState("");
  const [allowedExtensionsArray, setAllowedExtensionsArray] = useState([]);
  const [fileInvalidMessage, setFileInvalidMessage] = useState("");

  const handleFileChange = (e) => {
    const { files } = e?.target || {};

    const fileExtension = getFileExtension(files[0]?.name);

    const isExtensionAllowed = checkIfFileAllowed(fileExtension, allowedExtensionsArray);

    setFileInvalidMessage("");

    if (!isExtensionAllowed) {
      setFileInvalidMessage(`Incorrect file type. Allowed ${allowedExtensionsString}`);
    } else {
      const fileSizeMb = files[0]?.size / 1024 / 1024;

      if (fileSizeMb >= 3.8) {
        setFileInvalidMessage("File size exceeds 3.8 Mb");
      }
    }

    onChange && onChange(e, { validated: true });
  };

  useEffect(() => {
    axios
      .get(`${process.env.NEXT_PUBLIC_REACT_APP_API}/category/products/EIN_BOI/file-types`, {
        headers: { "Content-Type": "application/json" },
      })
      .then((response) => {
        if (response.status === 200) {
          response.data
            .filter((file) => file.code.includes("BOI_ID"))
            .forEach((allowedFile) => {
              setAllowedExtensionsString(getFileExtensionsString(allowedFile.allowedExtensions));
              setAllowedExtensionsArray(allowedFile.allowedExtensions);
            });
        }
      });
  }, []);

  return (
    <StyledBOIUpgradeForm id="BOIUploadForm" noValidate validated={values.validated} onSubmit={onSubmit}>
      <div className="content-body">
        <div className="content-block">
          <span>The BOI report is not currently required, but that may change again soon. Filing now ensures you&apos;re ahead of any future updates and protects your business from potential penalties if the requirement is reinstated.</span>
          <ul>
            <li>
              The Corporate Transparency Act (CTA) has changed multiple times — and there’s a strong chance the BOI filing requirement could return. Filing now puts you ahead of the curve and eliminates the risk of scrambling later.
            </li>
            <li>
              Submitting your BOI early gives you peace of mind, reduces the chance of legal exposure in the future, and helps maintain clean, compliant company records.
            </li>
            <li>
              If the requirement is reinstated, those who haven&apos;t filed may again face fines up to $500 per day, or even up to $10,000 total — and potential legal complications. Filing now alleviates the risk of future penalties.
            </li>
          </ul>
        </div>
        <div className="content-block">
          <span>Let us take care of it for you today — and stay ahead of the game.</span>
        </div>
        {values.isUpload && (
          <div className="content-block">
            <span>
              We will need an acceptable form of ID & your date of birth to submit this report - you may upload this now
              or later.&nbsp;
              <OverlayTrigger
                placement="auto"
                delay={{ show: 250, hide: 400 }}
                overlay={
                  <Tooltip id="overlay-example">
                    Accepted identification includes a state-issued driver&apos;s license, a state, local, or
                    tribe-issued ID, a US passport or, if none of the previous forms of identification are available, a
                    foreign passport.
                  </Tooltip>
                }
              >
                <StyledImg src={`${publicRuntimeConfig.basePath}/icons/V2/info-icon.svg`} alt="" />
              </OverlayTrigger>
            </span>
          </div>
        )}
        {values.isUpload && (
          <div className="content-block">
            {!isMobile && (
              <StyledRow>
                <Col xs={4}>
                  <Select
                    {...getFieldProps(FORM_FIELD_KEYS.dateOfBirthMonth)}
                    label="Date of Birth"
                    value={values[FORM_FIELD_KEYS.dateOfBirthMonth]}
                    validated={values.validated}
                    onChange={onChange}
                  />
                </Col>
                <Col xs={4}>
                  <Select
                    {...getFieldProps(FORM_FIELD_KEYS.dateOfBirthDay)}
                    value={values[FORM_FIELD_KEYS.dateOfBirthDay]}
                    emptyLabel
                    validated={values.validated}
                    options={getDayOptions({
                      year: values[FORM_FIELD_KEYS.dateOfBirthYear],
                      month: values[FORM_FIELD_KEYS.dateOfBirthMonth],
                    })}
                    onChange={onChange}
                  />
                </Col>
                <Col xs={4}>
                  <Select
                    {...getFieldProps(FORM_FIELD_KEYS.dateOfBirthYear)}
                    emptyLabel
                    options={getYearOptions()}
                    value={values[FORM_FIELD_KEYS.dateOfBirthYear]}
                    validated={values.validated}
                    onChange={onChange}
                  />
                </Col>
                <Col xs={12}>
                  <Upload
                    {...getFieldProps(FORM_FIELD_KEYS.customerIdFile)}
                    label="ID Upload"
                    defaultInvalidMessage={fileInvalidMessage || "File is not chosen"}
                    validated={values.validated}
                    invalid={Boolean(fileInvalidMessage)}
                    value={values[FORM_FIELD_KEYS.customerIdFile]}
                    onChange={handleFileChange}
                  />
                </Col>
                <Col xs={12}>
                  <TextInput
                    {...getFieldProps(FORM_FIELD_KEYS.driverLicenceOrID)}
                    validated={values.validated}
                    value={values[FORM_FIELD_KEYS.driverLicenceOrID]}
                    onChange={onChange}
                  />
                </Col>
                <Col xs={2} />
                <Col xs={8}>
                  <div className="divider" />
                  <StyledButton small color="#078FF1">
                    Upload Now
                  </StyledButton>
                </Col>
                <Col xs={2} />
              </StyledRow>
            )}
            {isMobile && (
              <StyledRow>
                <Col xs={12}>
                  <Select
                    {...getFieldProps(FORM_FIELD_KEYS.dateOfBirthMonth)}
                    label="Date of Birth"
                    value={values[FORM_FIELD_KEYS.dateOfBirthMonth]}
                    validated={values.validated}
                    onChange={onChange}
                  />
                </Col>
                <Col xs={12}>
                  <Select
                    {...getFieldProps(FORM_FIELD_KEYS.dateOfBirthDay)}
                    value={values[FORM_FIELD_KEYS.dateOfBirthDay]}
                    options={getDayOptions({
                      year: values[FORM_FIELD_KEYS.dateOfBirthYear],
                      month: values[FORM_FIELD_KEYS.dateOfBirthMonth],
                    })}
                    validated={values.validated}
                    onChange={onChange}
                  />
                </Col>
                <Col xs={12}>
                  <Select
                    {...getFieldProps(FORM_FIELD_KEYS.dateOfBirthYear)}
                    options={getYearOptions()}
                    value={values[FORM_FIELD_KEYS.dateOfBirthYear]}
                    validated={values.validated}
                    onChange={onChange}
                  />
                </Col>
                <Col xs={12}>
                  <Upload
                    {...getFieldProps(FORM_FIELD_KEYS.customerIdFile)}
                    label="ID Upload"
                    defaultInvalidMessage={fileInvalidMessage || "File is not chosen"}
                    validated={values.validated}
                    invalid={Boolean(fileInvalidMessage)}
                    value={values[FORM_FIELD_KEYS.customerIdFile]}
                    onChange={handleFileChange}
                  />
                </Col>
                <Col xs={12}>
                  <TextInput
                    {...getFieldProps(FORM_FIELD_KEYS.driverLicenceOrID)}
                    validated={values.validated}
                    value={values[FORM_FIELD_KEYS.driverLicenceOrID]}
                    onChange={onChange}
                  />
                </Col>
                <Col xs={12}>
                  <StyledButton small color="#078FF1">
                    Upload Now
                  </StyledButton>
                </Col>
              </StyledRow>
            )}
          </div>
        )}
        {values.isUpload && (
          <div className="content-block">
            <StyledRow>
              <Col xs={0} md={2} />
              <Col xs={12} md={8}>
                <StyledButton secondary onClick={onCancel}>
                  I Will Upload My ID Later
                </StyledButton>
              </Col>
              <Col xs={0} md={2} />
            </StyledRow>
          </div>
        )}
        {!values.isUpload && (
          <div className="content-block">
            <StyledRow>
              <Col xs={0} md={2} />
              <Col xs={12} md={8}>
                <StyledButton onClick={onConfirm}>Yes, please file for me - ${values.price}</StyledButton>
              </Col>
              <Col xs={0} md={2} />
            </StyledRow>
          </div>
        )}
        {!values.isUpload && (
          <div className="content-block">
            <StyledRow>
              <Col xs={0} md={2} />
              <Col xs={12} md={8}>
                <StyledButton secondary onClick={onCancel}>
                  No thanks, I will file myself
                </StyledButton>
              </Col>
              <Col xs={0} md={2} />
            </StyledRow>
          </div>
        )}
      </div>
    </StyledBOIUpgradeForm>
  );
}

export default BOIUpgradeForm;
